import type { Dispatch } from "react";
import type { EditorAction } from "../../editor/reducers";
import type { TocRegionCreationTabFragment } from "../../client/generated";
import { ScrollArea } from "@acdc2/ui/components/scroll-area";
import { NavbarTabCloseButton } from "../../shell/NavbarTabCloseButton";
import { NavbarTabContent } from "../../shell/NavbarTabContent";
import { useTranslation } from "react-i18next";
import { makeTabIdentifier } from "../../editor/reducers";
import { assertTypeName } from "../../client/guards";
import { RegionUpdateForm } from "../RegionUpdateForm";
import {
  useCreateTocRegionMutation,
  useTocRegionCreationTabSuspenseQuery,
} from "../../client/generated";

type Props = {
  fragment: TocRegionCreationTabFragment;
  dispatch: Dispatch<EditorAction>;
};

export function TOCRegionCreationTab({ fragment, dispatch }: Props) {
  const { t } = useTranslation();

  return (
    <NavbarTabContent
      value={makeTabIdentifier("toc", fragment.id)}
      title={t("TOCRegionCreationTab.title")}
      right={<NavbarTabCloseButton dispatch={dispatch} />}
    >
      <ScrollArea className="flex-1">
        <Body fragment={fragment} dispatch={dispatch} />
      </ScrollArea>
    </NavbarTabContent>
  );
}

function Body({ fragment, dispatch }: Props) {
  const tableOfContentsAttributeIds = fragment.attributes
    .filter((attr) => attr.__typename === "TableOfContentsAttribute")
    .map((attr) => attr.id);

  const { data } = useTocRegionCreationTabSuspenseQuery({
    variables: {
      regionId: fragment.id,
      tableOfContentsAttributeIds,
    },
  });

  assertTypeName(data.region?.__typename === "Region");

  const [createRegion] = useCreateTocRegionMutation();

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const result = await createRegion({
      variables: {
        regionId: fragment.id,
        excessAttributeIds: fragment.attributes.map((attr) => attr.id),
      },
      update: (cache) => {
        for (const attr of fragment.attributes) {
          const id = cache.identify(attr);
          cache.evict({ id });
          cache.gc();
        }
      },
    });

    return (
      !result.errors &&
      !result.data?.createTableOfContentsAttribute.userErrors?.length &&
      !result.data?.deleteAttributes.userErrors?.length
    );
  };

  return <RegionUpdateForm onSubmit={onSubmit} dispatch={dispatch} />;
}
